import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DocumentMagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

interface AuditLog {
  id: number;
  userId: number | null;
  username: string | null;
  action: string;
  method: string;
  route: string;
  originalUrl: string;
  ipAddress: string | null;
  userAgent: string | null;
  requestBody: object | null;
  params: object | null;
  query: object | null;
  statusCode: number | null;
  timestamp: string;
  user?: { // Optional user details if joined
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  };
}

interface PaginatedAuditLogsResponse {
  auditLogs: AuditLog[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

interface User {
  id: number;
  username: string;
  name: string;
}

const AuditLogView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [users, setUsers] = useState<User[]>([]); // For user filter dropdown
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(20); // Number of items per page

  // Filter state
  const [filters, setFilters] = useState({
    userId: searchParams.get('userId') || '',
    username: searchParams.get('username') || '',
    action: searchParams.get('action') || '',
    method: searchParams.get('method') || '',
    route: searchParams.get('route') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    ipAddress: searchParams.get('ipAddress') || '',
    statusCode: searchParams.get('statusCode') || '',
  });

  const fetchUsersForFilter = useCallback(async () => {
    try {
      // Fetch all users for the filter dropdown (no pagination needed for this list)
      const response = await axios.get<{ users: User[] }>('/users?limit=1000'); // Adjust limit as needed
      if (response.data && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users for filter:', err);
      setUsers([]);
    }
  }, []);

  const fetchAuditLogs = useCallback(async (page = currentPage, currentFilters = filters) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));

    // Add filters to params if they are set
    Object.entries(currentFilters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    
    setSearchParams(params, { replace: true });

    try {
      const response = await axios.get<PaginatedAuditLogsResponse>('/audit', { params });
      if (response.data && Array.isArray(response.data.auditLogs)) {
        setAuditLogs(response.data.auditLogs);
        setTotalPages(response.data.totalPages ?? 1);
        setCurrentPage(response.data.currentPage ?? 1);
        setTotalItems(response.data.totalItems ?? 0);
      } else {
        console.error('fetchAuditLogs received invalid data structure:', response.data);
        setAuditLogs([]);
        setTotalPages(1);
        setCurrentPage(1);
        setTotalItems(0);
        setError('Ошибка загрузки журнала аудита: неверный формат данных.');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Ошибка загрузки журнала аудита.');
      setAuditLogs([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, filters]);

  useEffect(() => {
    fetchUsersForFilter();
  }, [fetchUsersForFilter]);

  useEffect(() => {
    fetchAuditLogs(currentPage, filters);
  }, [currentPage, filters, fetchAuditLogs]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchAuditLogs(1, filters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      userId: '', username: '', action: '', method: '', route: '',
      startDate: '', endDate: '', ipAddress: '', statusCode: ''
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    // Create empty searchParams to clear URL
    const params = new URLSearchParams();
    setSearchParams(params, { replace: true });
    fetchAuditLogs(1, defaultFilters); // Refetch with default (empty) filters
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  const httpMethods = useMemo(() => ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'], []);

  if (loading && auditLogs.length === 0 && !error) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Журнал аудита действий пользователей"
        subtitle={`Всего записей: ${totalItems}`}
      />

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Filters Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-3 text-gray-700 flex items-center">
          <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
          Фильтры
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label htmlFor="userId" className="form-label">Пользователь (ID или выберите)</label>
            <select
              id="userId"
              name="userId"
              className="form-input w-full"
              value={filters.userId}
              onChange={handleFilterChange}
            >
              <option value="">Все пользователи</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name} ({user.username})</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="username" className="form-label">Пользователь (логин)</label>
            <input
              id="username"
              name="username"
              type="text"
              placeholder="Логин пользователя..."
              className="form-input w-full"
              value={filters.username}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="action" className="form-label">Действие</label>
            <input
              id="action"
              name="action"
              type="text"
              placeholder="Часть URL или описание..."
              className="form-input w-full"
              value={filters.action}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="method" className="form-label">Метод HTTP</label>
            <select
              id="method"
              name="method"
              className="form-input w-full"
              value={filters.method}
              onChange={handleFilterChange}
            >
              <option value="">Все методы</option>
              {httpMethods.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="route" className="form-label">Маршрут (API)</label>
            <input
              id="route"
              name="route"
              type="text"
              placeholder="/api/clients..."
              className="form-input w-full"
              value={filters.route}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="startDate" className="form-label">Дата начала</label>
            <input
              id="startDate"
              name="startDate"
              type="datetime-local"
              className="form-input w-full"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="endDate" className="form-label">Дата окончания</label>
            <input
              id="endDate"
              name="endDate"
              type="datetime-local"
              className="form-input w-full"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="ipAddress" className="form-label">IP адрес</label>
            <input
              id="ipAddress"
              name="ipAddress"
              type="text"
              placeholder="192.168.1.1"
              className="form-input w-full"
              value={filters.ipAddress}
              onChange={handleFilterChange}
            />
          </div>
          <div>
            <label htmlFor="statusCode" className="form-label">Код ответа HTTP</label>
            <input
              id="statusCode"
              name="statusCode"
              type="number"
              placeholder="200, 404..."
              className="form-input w-full"
              value={filters.statusCode}
              onChange={handleFilterChange}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            onClick={resetFilters}
            className="btn-secondary-outline inline-flex items-center text-sm"
            disabled={loading}
          >
            <XMarkIcon className="h-4 w-4 mr-1"/>
            Сбросить
          </button>
          <button
            type="button"
            onClick={applyFilters}
            className="btn-primary inline-flex items-center text-sm"
            disabled={loading}
          >
            <FunnelIcon className="h-4 w-4 mr-1"/>
            Применить фильтры
          </button>
        </div>
      </div>

      {/* Audit Logs Table */}
      {loading && auditLogs.length > 0 && <LoadingSpinner />}
      {!loading && auditLogs.length === 0 ? (
        <div className="text-center p-6 bg-white rounded-lg shadow">
          <p className="text-gray-500">
            {Object.values(filters).some(f => f) ? 'Записи, соответствующие критериям, не найдены.' : 'Журнал аудита пуст.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Время</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Пользователь</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Действие (URL)</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Метод</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Маршрут</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                  {/* Add more columns if needed, e.g., User Agent, Request Body (conditionally) */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(log.timestamp)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {log.user ? `${log.user.firstName || ''} ${log.user.lastName || ''} (${log.username || 'N/A'})` : (log.username || 'Система/Гость')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs" title={log.originalUrl}>{log.originalUrl}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.method}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs" title={log.route}>{log.route}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{log.ipAddress || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        log.statusCode && log.statusCode >= 200 && log.statusCode < 300 ? 'bg-green-100 text-green-800' :
                        log.statusCode && log.statusCode >= 400 && log.statusCode < 500 ? 'bg-yellow-100 text-yellow-800' :
                        log.statusCode && log.statusCode >= 500 ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {log.statusCode || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default AuditLogView;